import React from "react"
import { Modal } from "components/Core"
import { HiPlus, HiRefresh, HiOutlineSearch, HiClipboardCopy } from "react-icons/hi"
import DataGroup from "../DataGroup"
import DataItem from "../DataItem"

interface ProductPreviewModalProps {
  isOpen: boolean
  onClose: () => void
  selectedProduct: any
  loadingProduct: boolean
  skuTitle: string
}

const ProductPreviewModal = ({
  isOpen,
  onClose,
  selectedProduct,
  loadingProduct,
  skuTitle,
}: ProductPreviewModalProps) => (
  <Modal mounted={isOpen} className="!w-[500px] shadow-2xl rounded-3xl overflow-hidden">
    <div className="flex flex-col bg-[#F2F2F7] h-full max-h-[85vh]">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 bg-white/80 backdrop-blur-md sticky top-0 z-10">
        <div className="flex flex-row justify-between items-start">
          <div className="flex flex-col">
            <span className="text-[11px] font-bold text-blue-500 uppercase tracking-[0.2em] mb-1">
              Detalle del Producto
            </span>
            <h2 className="text-[24px] font-black text-gray-900 leading-none mb-1">
              {loadingProduct ? "Cargando..." : selectedProduct?.name || "No encontrado"}
            </h2>
            <p className="text-[15px] font-medium text-gray-500">SKU: {skuTitle || "---"}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200/50 rounded-full transition-colors text-gray-400">
            <HiPlus className="text-2xl transform rotate-45" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5">
        {loadingProduct ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4 opacity-50">
            <HiRefresh className="text-4xl text-blue-500 animate-spin" />
            <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">Consultando OSRM...</p>
          </div>
        ) : selectedProduct ? (
          <div className="flex flex-col gap-4">
            <DataGroup title="Información Base" themeColor="blue-500">
              <DataItem label="Nombre" value={selectedProduct.name} />
              <DataItem label="Habilitado" value={selectedProduct.status ? "Sí" : "No"} />
              <DataItem label="Tipo" value={selectedProduct.type} />
              <DataItem label="Retiro en Tienda" value={selectedProduct.storePickup ? "Sí" : "No"} last />
            </DataGroup>

            {selectedProduct.categories && (
              <DataGroup title="Categorías" themeColor="emerald-500">
                {Object.entries(selectedProduct.categories).map(([key, val], idx, arr) => (
                  <DataItem key={key} label={key} value={val} last={idx === arr.length - 1} />
                ))}
              </DataGroup>
            )}

            {selectedProduct.attributes && (
              <DataGroup title="Atributos" themeColor="amber-500">
                {Object.entries(selectedProduct.attributes).map(([key, val], idx, arr) => (
                  <DataItem key={key} label={key} value={val} last={idx === arr.length - 1} />
                ))}
              </DataGroup>
            )}

            {selectedProduct.preparationTime && Object.keys(selectedProduct.preparationTime).length > 0 && (
              <DataGroup title="Tiempos de Preparación" themeColor="indigo-500">
                {Object.entries(selectedProduct.preparationTime).map(([key, val], idx, arr) => (
                  <DataItem key={key} label={key} value={val} last={idx === arr.length - 1} />
                ))}
              </DataGroup>
            )}

            {selectedProduct.logisticAttributes && Object.keys(selectedProduct.logisticAttributes).length > 0 && (
              <DataGroup title="Atributos Logísticos" themeColor="rose-500">
                {Object.entries(selectedProduct.logisticAttributes).map(([key, val], idx, arr) => (
                  <DataItem key={key} label={key} value={val} last={idx === arr.length - 1} />
                ))}
              </DataGroup>
            )}

            {selectedProduct.safety && Object.keys(selectedProduct.safety).length > 0 && (
              <DataGroup title="Stock de Seguridad" themeColor="orange-500">
                {Object.entries(selectedProduct.safety).map(([key, val], idx, arr) => (
                  <DataItem key={key} label={key} value={val} last={idx === arr.length - 1} />
                ))}
              </DataGroup>
            )}

            <DataGroup title="Metadata Completa" themeColor="gray-400">
              <div className="relative group/metadata">
                <div className="p-4 bg-gray-50 rounded-xl overflow-hidden border border-gray-100">
                  <pre className="text-[10px] font-mono text-gray-400 overflow-x-auto whitespace-pre-wrap leading-tight">
                    {JSON.stringify(selectedProduct, null, 2)}
                  </pre>
                </div>
                <button 
                  onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(selectedProduct, null, 2));
                  }}
                  className="absolute top-2 right-2 p-2 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg text-gray-500 hover:text-blue-600 hover:border-blue-200 shadow-sm opacity-0 group-hover/metadata:opacity-100 transition-all duration-300 flex items-center gap-1.5"
                >
                  <HiClipboardCopy className="text-sm" />
                  <span className="text-[10px] font-bold uppercase">Copiar JSON</span>
                </button>
              </div>
            </DataGroup>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center px-10">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <HiOutlineSearch className="text-3xl text-gray-300" />
            </div>
            <h4 className="text-lg font-bold text-gray-800 mb-1">Sin información</h4>
            <p className="text-sm font-medium text-gray-400">
              No pudimos encontrar detalles extendidos para este SKU en el inventario.
            </p>
          </div>
        )}
      </div>
    </div>
  </Modal>
)

export default ProductPreviewModal
